import React, { useCallback, useEffect, useRef, useState } from "react";
import useFetch from "hooks/useFetch";
import {
  Badge,
  Button,
  Card,
  Column,
  EmptyState,
  Icon,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
  Table,
  TextField,
} from "stories/Admin";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import PageModal from "../LocationsPage/PageModal";
import {
  buildQuery,
  formatLogDate,
  kindInfo,
  kindLabel,
  PushLogEntry,
  PushLogPage as PushLogPageData,
  recipientText,
  topicsOf,
  statusInfo,
} from "./pushLog";
import styles from "./PushLogPage.module.scss";

/*
 * Superadmin: alt iBønn har pushet, nyeste først. Manuelle push fra moskeer og superadmin,
 * janazah-varsler og (valgfritt) stille jamat-oppdateringer. Fra GET api/v1/admin/push-log.
 */

const PAGE_SIZE = 50;

const kindOptions = [
  { value: "", label: "Alle varsler" },
  { value: "MOSQUE", label: "Fra moskeer" },
  { value: "ALL", label: "Til alle brukere" },
  { value: "ADMINS", label: "Til admins" },
  { value: "DIRECT", label: "Til én bruker" },
  { value: "JANAZAH", label: "Janazah" },
  { value: "TOPIC", label: "Tema (f.eks. Mawaqeet)" },
  { value: "JAMAT", label: "Stille jamat-oppdateringer" },
  { value: "SILENT", label: "Alt, også stille" },
  { value: "UNKNOWN", label: "Eldre uten type" },
];

const statusOptions = [
  { value: "", label: "Alle statuser" },
  { value: "SENT", label: "Sendt" },
  { value: "PARTIAL", label: "Delvis" },
  { value: "FAILED", label: "Feilet" },
];

const nf = (n: number) => n.toLocaleString("nb-NO");

const PushLogPage: React.FC = () => {
  const { execute } = useFetch<PushLogPageData>();
  const executeRef = useRef(execute);
  executeRef.current = execute;

  const [data, setData] = useState<PushLogPageData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [details, setDetails] = useState<PushLogEntry | null>(null);

  // Søk først når man slutter å skrive.
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setIsLoading(true);
    const result = await executeRef.current(
      buildQuery({ kind, status, q: query, page: page - 1, size: PAGE_SIZE }),
      "GET",
    );
    if (result) setData(result);
    setFailed(!result);
    setIsLoading(false);
  }, [kind, status, query, page]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: Array<Column<PushLogEntry>> = [
    {
      key: "date",
      header: "Tidspunkt",
      numeric: true,
      render: (e) => formatLogDate(e.date),
    },
    {
      key: "kind",
      header: "Type",
      render: (e) => (
        <Badge tone={kindInfo[e.kind]?.tone ?? "grey"}>
          {kindLabel(e.kind)}
        </Badge>
      ),
    },
    {
      key: "to",
      header: "Til",
      render: (e) => (
        <span className={styles.stack}>
          <span>{recipientText(e)}</span>
          {topicsOf(e.topic).length > 1 ? (
            <span className={styles.muted}>
              {topicsOf(e.topic).join(" + ")}
            </span>
          ) : (
            e.orgNr &&
            e.mosqueName && <span className={styles.muted}>{e.orgNr}</span>
          )}
        </span>
      ),
    },
    {
      key: "msg",
      header: "Melding",
      render: (e) => (
        <span className={styles.stack}>
          <b className={styles.clamp}>{e.title || "–"}</b>
          {e.body && (
            <span className={`${styles.muted} ${styles.clamp}`}>{e.body}</span>
          )}
        </span>
      ),
    },
    {
      key: "by",
      header: "Sendt av",
      render: (e) =>
        e.sentBy ? (
          <span className={styles.clamp}>{e.sentBy}</span>
        ) : (
          <span className={styles.muted}>
            {e.kind === "JANAZAH" || e.kind === "JAMAT"
              ? "Automatisk"
              : "Ukjent"}
          </span>
        ),
    },
    {
      key: "status",
      header: "Status",
      render: (e) => {
        const s = statusInfo(e);
        return <Badge tone={s.tone}>{s.label}</Badge>;
      },
    },
  ];

  const counts = data?.last30Days ?? [];
  const visible = counts.filter((c) => c.kind !== "JAMAT");
  const totalVisible = visible.reduce((sum, c) => sum + c.sent, 0);
  const failedTotal = counts.reduce((sum, c) => sum + c.failed, 0);
  const silent = counts.find((c) => c.kind === "JAMAT");
  const pageCount = data ? Math.ceil(data.total / PAGE_SIZE) : 0;

  return (
    <>
      <PageHeader
        title="Push-meldinger"
        subtitle="Alt iBønn har pushet: fra moskeer, til alle brukere, til admins og janazah-varsler."
        actions={
          <Button
            variant="secondary"
            icon={<Icon name="swap" size={16} />}
            onClick={load}
            loading={isLoading}
          >
            Oppdater
          </Button>
        }
      />
      <SectionTabs />

      <div className={styles.stats}>
        <div className={styles.stat}>
          <b>{data ? nf(totalVisible) : "–"}</b>
          <span>Varsler siste 30 dager</span>
        </div>
        {visible
          .filter((c) => c.sent > 0)
          .slice(0, 2)
          .map((c) => (
            <div key={c.kind} className={styles.stat}>
              <b>{nf(c.sent)}</b>
              <span>{kindLabel(c.kind)}</span>
            </div>
          ))}
        <div className={styles.stat}>
          <b>{data ? nf(failedTotal) : "–"}</b>
          <span>Feilet siste 30 dager</span>
          {silent && silent.sent > 0 && (
            <small className={styles.muted}>
              + {nf(silent.sent)} stille jamat-push
            </small>
          )}
        </div>
      </div>

      <Card className={styles.tableCard}>
        <div className={styles.filters}>
          <TextField
            label="Søk"
            placeholder="Søk i tittel, melding, moské eller tema"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fieldClassName={styles.searchField}
          />
          <Select
            label="Type"
            value={kind}
            options={kindOptions}
            onChange={(e) => {
              setKind(e.target.value);
              setPage(1);
            }}
          />
          <Select
            label="Status"
            value={status}
            options={statusOptions}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
          />
        </div>

        {failed && !data ? (
          <EmptyState icon="alert" title="Kunne ikke hente push-loggen">
            Prøv igjen med «Oppdater».
          </EmptyState>
        ) : isLoading && !data ? (
          <div className={styles.skeletons}>
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} height={44} />
            ))}
          </div>
        ) : (
          <>
            <Table
              columns={columns}
              rows={data?.items ?? []}
              rowKey={(e) => e.id}
              onRowClick={(e) => setDetails(e)}
              rowState={(e) => (e.kind === "JAMAT" ? "dim" : undefined)}
              caption="Push-logg"
              empty={
                <EmptyState icon="bell" title="Ingen push">
                  Ingen push passer filtrene.
                </EmptyState>
              }
            />
            <div className={styles.footer}>
              <span className={styles.muted}>
                {data ? `${nf(data.total)} push totalt` : ""}
              </span>
              <Pagination
                page={page}
                pageCount={pageCount}
                onChange={setPage}
              />
            </div>
          </>
        )}
      </Card>

      <PageModal
        open={!!details}
        title={details?.title || "Push"}
        icon="bell"
        onClose={() => setDetails(null)}
      >
        {details && (
          <dl className={styles.details}>
            <dt>Tidspunkt</dt>
            <dd>{formatLogDate(details.date)}</dd>
            <dt>Type</dt>
            <dd>{kindLabel(details.kind)}</dd>
            <dt>Til</dt>
            <dd>{recipientText(details)}</dd>
            {details.topic && (
              <>
                <dt>
                  {topicsOf(details.topic).length > 1
                    ? "FCM-temaer"
                    : "FCM-tema"}
                </dt>
                <dd>
                  <code>{topicsOf(details.topic).join(", ")}</code>
                </dd>
              </>
            )}
            <dt>Status</dt>
            <dd>
              {statusInfo(details).label}
              {details.recipients != null &&
                ` · ${nf(details.recipients - (details.failed ?? 0))} av ${nf(details.recipients)} levert`}
            </dd>
            {details.error && (
              <>
                <dt>Feil</dt>
                <dd className={styles.error}>{details.error}</dd>
              </>
            )}
            <dt>Sendt av</dt>
            <dd>{details.sentBy || "Automatisk"}</dd>
            {details.body && (
              <>
                <dt>Melding</dt>
                <dd className={styles.body}>{details.body}</dd>
              </>
            )}
            {details.imgurl && (
              <>
                <dt>Bilde</dt>
                <dd>
                  <a href={details.imgurl} target="_blank" rel="noreferrer">
                    {details.imgurl}
                  </a>
                </dd>
              </>
            )}
          </dl>
        )}
      </PageModal>
    </>
  );
};

export default PushLogPage;
